import { useState, useRef } from "react";
import { api, fileUrl, formatApiError } from "@/lib/api";
import { CategoryBadge, PriorityBadge, StatusBadge } from "@/components/Badges";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  X, PencilSimple, Trash, Paperclip, Microphone, Stop, PlayCircle, FileText,
   ClockCounterClockwise, PaperPlaneRight, CheckCircle, Spinner, ArrowClockwise, User, Calendar, ClipboardText, SealCheck,
} from "@phosphor-icons/react";

const HISTORY_ICON = {
  created: PlayCircle, edited: PencilSimple, status_change: ArrowClockwise,
  note_added: FileText, file_added: Paperclip, voice_added: Microphone, date_moved: Calendar, reviewed: SealCheck,
};

export default function TaskDetail({ task, onClose, onChanged, onEdit }) {
  const { user } = useAuth();
  const isManager = ["owner", "store_manager", "assistant_manager"].includes(user.role);
  const [note, setNote] = useState("");
  const [reviewNote, setReviewNote] = useState("");
  const [recording, setRecording] = useState(false);
  const [uploading, setUploading] = useState(false);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);

  const refresh = async () => {
    const { data } = await api.get(`/tasks/${task.id}`);
    onChanged(data);
  };

  const setStatus = async (status) => {
    try { await api.patch(`/tasks/${task.id}/status`, { status }); toast.success("Status updated"); refresh(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const review = async (action) => {
    try {
      await api.patch(`/tasks/${task.id}/review`, { action, note: reviewNote });
      toast.success(action === "approve" ? "Task approved" : "Task reopened");
      setReviewNote("");
      refresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const addNote = async () => {
    if (!note.trim()) return;
    await api.post(`/tasks/${task.id}/notes`, { text: note });
    setNote(""); refresh(); toast.success("Note added");
  };

  const del = async () => {
    if (!window.confirm("Delete this task?")) return;
    await api.delete(`/tasks/${task.id}`); toast.success("Task deleted"); onClose(); onChanged(null);
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData(); fd.append("file", file);
    try { await api.post(`/tasks/${task.id}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("File uploaded"); refresh(); }
    catch (err) { toast.error("Upload failed"); } finally { setUploading(false); }
  };

  const startRec = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (ev) => chunksRef.current.push(ev.data);
      mr.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        stream.getTracks().forEach((t) => t.stop());
        setUploading(true);
        const fd = new FormData(); fd.append("file", new File([blob], "voice.webm", { type: "audio/webm" }));
        try { await api.post(`/tasks/${task.id}/voice`, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Voice note transcribed"); refresh(); }
        catch (err) { toast.error("Voice upload failed"); } finally { setUploading(false); }
      };
      mr.start(); mediaRef.current = mr; setRecording(true);
    } catch (err) { toast.error("Microphone access denied"); }
  };
  const stopRec = () => { mediaRef.current?.stop(); setRecording(false); };

  const canUpdateStatus = isManager || task.assigned_to === user.id;

  return (
    <div className="fixed inset-0 z-[60] bg-black/40 flex items-end md:items-center justify-center" onClick={onClose}>
      <div data-testid="task-detail" onClick={(e) => e.stopPropagation()} className="bg-white border-2 border-black md:rounded-xl rounded-t-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b-2 border-black px-5 py-4 flex items-center justify-between z-10">
          <h2 className="font-display text-xl font-bold pr-2 leading-tight">{task.title}</h2>
          <div className="flex items-center gap-2">
            {isManager && <button data-testid="edit-task-btn" onClick={() => onEdit(task)} className="press w-9 h-9 rounded-full border-2 border-black flex items-center justify-center"><PencilSimple size={16} weight="bold" /></button>}
            {isManager && <button data-testid="delete-task-btn" onClick={del} className="press w-9 h-9 rounded-full border-2 border-black bg-primary text-white flex items-center justify-center"><Trash size={16} weight="bold" /></button>}
            <button data-testid="close-detail-btn" onClick={onClose} className="press w-9 h-9 rounded-full border-2 border-black flex items-center justify-center"><X size={18} weight="bold" /></button>
          </div>
        </div>

        <div className="p-5 space-y-5">
          <div className="flex flex-wrap gap-2">
            <CategoryBadge category={task.category} />
            <PriorityBadge priority={task.priority} />
            <StatusBadge status={task.status} />
          </div>

          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="flex items-center gap-2"><Calendar size={16} weight="bold" /><span className="font-semibold">{task.due_date || "No date"}</span></div>
            <div className="flex items-center gap-2"><User size={16} weight="bold" /><span className="font-semibold">{task.assigned_to_name || "Unassigned"}</span></div>
            {(task.start_time || task.end_time) && <div className="col-span-2 text-black/60">{task.start_time} {task.end_time ? `– ${task.end_time}` : ""} {task.time_frame ? `· ${task.time_frame}` : ""}</div>}
          </div>

          {task.notes && <div className="bg-[#F9F9F6] border-2 border-black rounded-lg p-4 text-sm whitespace-pre-wrap">{task.notes}</div>}

          {(task.customer_name || task.location_name || task.equipment_name || task.vendor_name || (task.tags?.length > 0)) && (
            <div data-testid="task-detail-fields" className="grid grid-cols-2 gap-3 text-sm border-2 border-black rounded-lg p-4 bg-white">
              {task.customer_name && <div><p className="text-[10px] font-bold uppercase tracking-wider text-black/40">Customer</p><p className="font-semibold">{task.customer_name}</p></div>}
              {task.location_name && <div><p className="text-[10px] font-bold uppercase tracking-wider text-black/40">Location</p><p className="font-semibold">{task.location_name}</p></div>}
              {task.equipment_name && <div><p className="text-[10px] font-bold uppercase tracking-wider text-black/40">Equipment</p><p className="font-semibold">{task.equipment_name}{task.equipment_type ? ` · ${task.equipment_type}` : ""}</p></div>}
              {task.vendor_name && <div><p className="text-[10px] font-bold uppercase tracking-wider text-black/40">Vendor</p><p className="font-semibold">{task.vendor_name}</p></div>}
              {task.subcategory && <div><p className="text-[10px] font-bold uppercase tracking-wider text-black/40">Subcategory</p><p className="font-semibold">{task.subcategory}</p></div>}
              {task.tags?.length > 0 && <div className="col-span-2"><p className="text-[10px] font-bold uppercase tracking-wider text-black/40 mb-1">Tags</p><div className="flex flex-wrap gap-1">{task.tags.map((t, i) => <span key={i} className="text-xs font-bold px-2 py-0.5 rounded-full border-2 border-black bg-[#F3F4F6]">{t}</span>)}</div></div>}
            </div>
          )}

          {/* Status actions */}
          {canUpdateStatus && (
            <div className="flex gap-2">
              <button data-testid="mark-progress-btn" onClick={() => setStatus("in_progress")} className="press flex-1 h-11 rounded-full bg-[#FEF9C3] font-bold text-sm border-2 border-black flex items-center justify-center gap-2"><Spinner size={16} weight="bold" /> In Progress</button>
              <button data-testid="mark-complete-btn" onClick={() => setStatus("completed")} className="press flex-1 h-11 rounded-full bg-[#DCFCE7] font-bold text-sm border-2 border-black flex items-center justify-center gap-2"><CheckCircle size={16} weight="bold" /> Complete</button>
            </div>
          )}

          {/* Manager review panel */}
          {isManager && task.status === "completed" && (
            <div data-testid="review-panel" className="border-2 border-black rounded-xl bg-[#F3E8FF] p-4">
              <div className="flex items-center gap-2 mb-2">
                <ClipboardText size={18} weight="bold" />
                <h3 className="font-display font-bold">Manager Review</h3>
                {task.reviewed && <span className="ml-auto inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full bg-[#DCFCE7] border-2 border-black"><SealCheck size={13} weight="fill" /> Approved</span>}
              </div>
              {task.reviewed ? (
                <p className="text-sm text-black/70">Approved by {task.reviewed_by} · {task.reviewed_at ? new Date(task.reviewed_at).toLocaleString() : ""}</p>
              ) : (
                <>
                  <textarea data-testid="review-note-input" value={reviewNote} onChange={(e) => setReviewNote(e.target.value)} placeholder="Add a review note (optional)…" className="w-full min-h-[64px] p-3 rounded-lg border-2 border-black bg-white outline-none focus:ring-2 focus:ring-primary text-sm mb-3" />
                  <div className="flex gap-2">
                    <button data-testid="review-approve-btn" onClick={() => review("approve")} className="press flex-1 h-11 rounded-full bg-[#DCFCE7] font-bold text-sm border-2 border-black flex items-center justify-center gap-2"><SealCheck size={16} weight="bold" /> Approve</button>
                    <button data-testid="review-reopen-btn" onClick={() => review("reopen")} className="press flex-1 h-11 rounded-full bg-primary text-white font-bold text-sm border-2 border-black flex items-center justify-center gap-2"><ArrowClockwise size={16} weight="bold" /> Reopen</button>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Attachments */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-xs font-bold uppercase tracking-[0.15em] text-black/60">Photos & Files</h3>
              <label data-testid="upload-file-btn" className="press cursor-pointer inline-flex items-center gap-1 text-xs font-bold px-3 py-1 rounded-full bg-white border-2 border-black">
                <Paperclip size={14} weight="bold" /> {uploading ? "…" : "Add"}
                <input type="file" className="hidden" onChange={onFile} />
              </label>
            </div>
            {task.attachments?.length > 0 ? (
              <div className="grid grid-cols-3 gap-2">
                {task.attachments.map((a) => (
                  <a key={a.id} href={fileUrl(a.storage_path)} target="_blank" rel="noreferrer" className="press block border-2 border-black rounded-lg overflow-hidden bg-white">
                    {a.is_image ? <img src={fileUrl(a.storage_path)} alt={a.filename} className="w-full h-20 object-cover" />
                      : <div className="h-20 flex flex-col items-center justify-center gap-1 p-1"><FileText size={22} /><span className="text-[10px] truncate w-full text-center px-1">{a.filename}</span></div>}
                  </a>
                ))}
              </div>
            ) : <p className="text-sm text-black/40">No files yet.</p>}
          </div>

          {/* Voice notes */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-xs font-bold uppercase tracking-[0.15em] text-black/60">Voice Notes</h3>
              {!recording ? (
                <button data-testid="record-voice-btn" onClick={startRec} disabled={uploading} className="press inline-flex items-center gap-1 text-xs font-bold px-3 py-1 rounded-full bg-[#E0F2FE] border-2 border-black"><Microphone size={14} weight="fill" /> Record</button>
              ) : (
                <button data-testid="stop-voice-btn" onClick={stopRec} className="press inline-flex items-center gap-1 text-xs font-bold px-3 py-1 rounded-full bg-primary text-white border-2 border-black animate-pulse"><Stop size={14} weight="fill" /> Stop</button>
              )}
            </div>
            {task.voice_notes?.length > 0 ? (
              <div className="space-y-2">
                {task.voice_notes.map((v) => (
                  <div key={v.id} className="border-2 border-black rounded-lg p-3 bg-white">
                    <audio controls src={fileUrl(v.storage_path)} className="w-full h-9" />
                    {v.transcript && <p className="text-sm mt-2 flex gap-2"><FileText size={16} className="shrink-0 mt-0.5" /><span>{v.transcript}</span></p>}
                  </div>
                ))}
              </div>
            ) : <p className="text-sm text-black/40">No voice notes yet.</p>}
          </div>

          {/* Add note */}
          <div>
            <h3 className="text-xs font-bold uppercase tracking-[0.15em] text-black/60 mb-2">Add a note</h3>
            <div className="flex gap-2">
              <input data-testid="note-input" value={note} onChange={(e) => setNote(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addNote()} className="flex-1 h-11 px-3 rounded-lg border-2 border-black bg-white outline-none focus:ring-2 focus:ring-primary text-sm" placeholder="Write a note…" />
              <button data-testid="add-note-btn" onClick={addNote} className="press w-11 h-11 rounded-lg bg-secondary text-white border-2 border-black flex items-center justify-center"><PaperPlaneRight size={18} weight="bold" /></button>
            </div>
          </div>

          {/* History timeline */}
          <div>
            <h3 className="text-xs font-bold uppercase tracking-[0.15em] text-black/60 mb-3 flex items-center gap-2"><ClockCounterClockwise size={16} weight="bold" /> Full History</h3>
            <div className="border-l-2 border-black/20 ml-3 space-y-4">
              {[...(task.history || [])].reverse().map((h) => {
                const Icon = HISTORY_ICON[h.action] || FileText;
                return (
                  <div key={h.id} className="relative pl-6" data-testid={`history-${h.id}`}>
                    <div className="absolute -left-[13px] top-0 w-6 h-6 rounded-full bg-white border-2 border-black flex items-center justify-center"><Icon size={12} weight="bold" /></div>
                    <p className="text-sm font-semibold">{h.detail}</p>
                    <p className="text-xs text-black/50">{h.by} · {new Date(h.timestamp).toLocaleString()}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
